// signatureRequestService.ts

import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { csl } from "lobbie";
import { BASE_API_URL, BASE_SECURE_ROUTE } from "src/constants/api";
import { handleBackendHttpError } from "src/redux/handleBackendHttpError";
import { triggerSearchTableRefetch } from "src/redux/slices/searchTableRefetchSlice";
import { RootState } from "src/redux/store";
import { ECSLSignatureRequestType, ESearchTableType } from "src/types/global_enums";
import { handleError, notify } from "src/utils";
import { createDrawerThunkMeta } from "../stackableDrawerConstant";

export interface IBaseSignatureState {
    isSignatureMode: boolean;
}

export interface IPrimarySignatureState extends IBaseSignatureState {
    primarySignatureRequestId?: number;
    primarySignee?: csl.ISignatureRequestSigner;
    primarySignatureBlob?: string;
    primarySignedDateTimeISO?: string;
    allSignaturesPreviouslySigned: boolean;
}

export interface IWitnessSignatureState extends IPrimarySignatureState {
    witnessSignatureRequestId?: number;
    witnessSignee?: csl.ISignatureRequestSigner;
    witnessSignatureBlob?: string;
    witnessSignedDateTimeISO?: string;
}

export interface INullificationCapableSlice {
    isNullificationMode: boolean;
    isNullificationViewMode: boolean;
    nullificationError: string | null;
}

function isPrimarySignatureState(state: any): state is IPrimarySignatureState {
    return (
        "primarySignee" in state &&
        "primarySignatureBlob" in state &&
        "primarySignedDateTimeISO" in state &&
        "primarySignatureRequestId" in state
    );
}
function isNullificationCapableState(state: any): state is INullificationCapableSlice {
    return "isNullificationMode" in state && "isNullificationViewMode" in state;
}

function isWitnessSignatureState(state: any): state is IWitnessSignatureState {
    return (
        "witnessSignee" in state &&
        "witnessSignatureBlob" in state &&
        "witnessSignedDateTimeISO" in state &&
        "witnessSignatureRequestId" in state
    );
}

export const initialPrimarySignatureState = {
    isSignatureMode: false,
    primarySignatureRequestId: undefined,
    primarySignee: undefined,
    primarySignatureBlob: undefined,
    primarySignedDateTimeISO: undefined,
    allSignaturesPreviouslySigned: false,
};

export const initialWitnessSignatureState = {
    ...initialPrimarySignatureState,
    witnessSignatureRequestId: undefined,
    witnessSignee: undefined,
    witnessSignatureBlob: undefined,
    witnessSignedDateTimeISO: undefined,
};

export const initialNullificationCapableState = {
    isNullificationMode: false,
    isNullificationViewMode: false,
    nullificationError: null,
};

export interface ISignatureRequestPayload {
    signatureRequestId: number;
    signatureBlob: string;
    ipAddress: string;
}

export const getSignatureBlobForRequest = <T extends IPrimarySignatureState>(
    signatureRequest: csl.ISignatureRequest,
    state: T,
    matchOnSignerId = true,
): string | undefined => {
    const isPrimary = signatureRequest.signatureType === ECSLSignatureRequestType.PRIMARY;
    const isWitness = signatureRequest.signatureType === ECSLSignatureRequestType.WITNESS;
    // Primary signature - try ID match first, then signer ID match
    if (isPrimary) {
        if (signatureRequest.id === state.primarySignatureRequestId) {
            return state.primarySignatureBlob;
        }
        // Fallback: match by signer ID
        if (
            matchOnSignerId &&
            signatureRequest.signer?.id === state.primarySignee?.id &&
            state.primarySignatureBlob
        ) {
            return state.primarySignatureBlob;
        }
    }
    // Witness signature - similar logic
    if (isWitness && isWitnessSignatureState(state)) {
        const witnessState = state as IWitnessSignatureState;
        if (signatureRequest.id === witnessState.witnessSignatureRequestId) {
            return witnessState.witnessSignatureBlob;
        }
        // Fallback: match by signer ID
        if (
            matchOnSignerId &&
            signatureRequest.signer?.id === witnessState.witnessSignee?.id &&
            witnessState.witnessSignatureBlob
        ) {
            return witnessState.witnessSignatureBlob;
        }
    }
    return undefined;
};

/*
 * Create signature payloads for the provided signature requests and state,
 * for signature requests that were just created for example creating a new container log -> match on signer ID to find what signature blob matches what signature request
 */
export const createSignaturePayloads = <T extends IPrimarySignatureState>(
    requests: csl.ISignatureRequest | csl.ISignatureRequest[],
    state: T,
    matchOnSignerId = true,
): ISignatureRequestPayload[] => {
    // Convert single request to array for consistent handling
    const requestArray = Array.isArray(requests) ? requests : [requests];
    // Map requests to payloads
    const payloads = requestArray
        .map((request) => {
            const blob = getSignatureBlobForRequest(request, state, matchOnSignerId);
            if (!blob) return null;
            return {
                signatureRequestId: request.id,
                signatureBlob: blob,
            };
        })
        .filter(Boolean) as ISignatureRequestPayload[];
    // Filter out duplicate signature requests by keeping only the first occurrence of each signatureRequestId
    const uniquePayloads = payloads.filter(
        (payload, index, self) =>
            index === self.findIndex((p) => p.signatureRequestId === payload.signatureRequestId),
    );
    return uniquePayloads;
};

export const submitBatchSignatures = async (
    signaturePayloads: ISignatureRequestPayload[],
    options: {
        showSuccessNotification?: boolean;
        successMessage?: string;
        handleErrors?: boolean;
    } = {},
): Promise<void> => {
    const {
        showSuccessNotification = true,
        successMessage = "Signatures submitted successfully",
        handleErrors = true,
    } = options;
    if (signaturePayloads.length === 0) {
        return;
    }
    try {
        await axios.post(
            `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/csl/signature-request/batch-sign`,
            { signatureRequests: signaturePayloads },
            { withCredentials: true },
        );
        if (showSuccessNotification) {
            notify({
                level: "success",
                title: "Success",
                message: successMessage,
            });
        }
    } catch (error) {
        if (handleErrors) {
            handleBackendHttpError(error);
            throw new Error("Failed to submit signatures");
        } else {
            throw error;
        }
    }
};

export const createStartSignatureRequestThunk = (actionType: string, forNullification = false) => {
    return createAsyncThunk<csl.ISignatureRequestData, number, { rejectValue: string }>(
        actionType,
        async (signatureRequestId: number, { rejectWithValue }) => {
            try {
                const route = `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/csl/signature-request/${signatureRequestId}/related-container-logs`;
                const { data } = await axios.get<csl.ISignatureRequestData>(route, {
                    withCredentials: true,
                });
                if (!data?.containerLogs?.length) {
                    notify({
                        level: "error",
                        title: "Error",
                        message: `No container entries found for this ${forNullification ? "nullification " : ""}signature request`,
                    });
                    return rejectWithValue("No container entries found");
                }
                if (forNullification && !data.signatureRequest.forNullification) {
                    notify({
                        level: "error",
                        title: "Error",
                        message: "The provided signature request is not for nullification",
                    });
                    return rejectWithValue("Not a nullification signature request");
                }
                return data;
            } catch (error: any) {
                console.error(error);
                notify({
                    level: "error",
                    title: "Error",
                    message: "Failed to load signature request details",
                });
                return rejectWithValue("Failed to load signature request details");
            }
        },
        createDrawerThunkMeta("show"),
    );
};
export const createSubmitSignatureRequestThunk = <T extends keyof RootState>(
    actionType: string,
    sliceName: T,
    tablesToRefresh: ESearchTableType[] = [],
) => {
    return createAsyncThunk<void, void, { state: RootState; rejectValue: string }>(
        actionType,
        async (_, { getState, dispatch, rejectWithValue }) => {
            const state = getState();
            const currentUser = state.user?.user;
            const sliceState = state[sliceName];
            if (!isPrimarySignatureState(sliceState)) {
                return rejectWithValue(
                    `State slice ${String(sliceName)} is not a valid signature state`,
                );
            }
            if (!currentUser) {
                return rejectWithValue("User information not available");
            }
            // Determine if we need to handle witness signature state
            const hasWitnessFields = isWitnessSignatureState(sliceState);
            const sigState = hasWitnessFields
                ? (sliceState as unknown as IWitnessSignatureState)
                : (sliceState as unknown as IPrimarySignatureState);

            try {
                // Collect signature requests to be signed
                const requestsForUser: csl.ISignatureRequest[] = [];
                // Add primary signature request if available
                if (
                    sigState.primarySignatureRequestId &&
                    sigState.primarySignee?.id === currentUser.id &&
                    sigState.primarySignatureBlob
                ) {
                    const primaryRequest: csl.ISignatureRequest = {
                        id: sigState.primarySignatureRequestId,
                        signer: sigState.primarySignee,
                        signatureType: ECSLSignatureRequestType.PRIMARY,
                    } as csl.ISignatureRequest;
                    requestsForUser.push(primaryRequest);
                }
                // Add witness signature request if available
                if (hasWitnessFields) {
                    const witnessState = sigState as IWitnessSignatureState;
                    if (
                        witnessState.witnessSignatureRequestId &&
                        witnessState.witnessSignee?.id === currentUser.id &&
                        witnessState.witnessSignatureBlob
                    ) {
                        const witnessRequest: csl.ISignatureRequest = {
                            id: witnessState.witnessSignatureRequestId,
                            signer: witnessState.witnessSignee,
                            signatureType: ECSLSignatureRequestType.WITNESS,
                        } as csl.ISignatureRequest;
                        requestsForUser.push(witnessRequest);
                    }
                }
                if (requestsForUser.length === 0) {
                    return rejectWithValue("No signature request found for the current user");
                }
                // Create signature payloads for the current user's requests
                const payloads = createSignaturePayloads(requestsForUser, sigState, false);
                if (payloads.length === 0) {
                    return rejectWithValue("Missing signature data for your signature request");
                }
                await submitBatchSignatures(payloads);
                if (tablesToRefresh.length > 0) {
                    dispatch(triggerSearchTableRefetch(tablesToRefresh));
                }
            } catch (error: any) {
                handleBackendHttpError(error);
                return rejectWithValue("Failed to submit signature");
            }
        },
        createDrawerThunkMeta("hide"),
    );
};

export const processSignatureData = <T extends IPrimarySignatureState>(
    state: T,
    data: csl.ISignatureRequestData | csl.ISignatureRequest[],
    options: {
        isNullificationMode?: boolean;
        mainSignatureRequest?: csl.ISignatureRequest;
    } = {},
): T => {
    // Get the options with defaults
    const { isNullificationMode = false, mainSignatureRequest = undefined } = options;
    // Normalize input to array of signature requests
    let signatureRequests = Array.isArray(data)
        ? data
        : data.containerLogs?.flatMap((log) => log.signatureRequests) || [];
    // Get main signature request if provided in data
    const mainRequestFromData =
        !Array.isArray(data) && data.signatureRequest ? data.signatureRequest : undefined;
    // Filter signature requests for nullification mode
    if (isNullificationMode) {
        signatureRequests = signatureRequests.filter((sr) => sr.forNullification === true);
        if (signatureRequests.length === 0) {
            handleError(new Error("No nullification signature requests found"));
            return state;
        }
    } else {
        signatureRequests = signatureRequests.filter((sr) => !sr.forNullification);
    }
    const hasWitnessRequests = signatureRequests.some(
        (sr) => sr.signatureType === ECSLSignatureRequestType.WITNESS,
    );
    const primaryRequest = signatureRequests.find(
        (sr) => sr.signatureType === ECSLSignatureRequestType.PRIMARY,
    );
    // Use provided main request or the one from data, or the found primary request
    const finalMainRequest = mainSignatureRequest || mainRequestFromData || primaryRequest;
    if (!primaryRequest && !finalMainRequest) {
        return state; // No valid requests to process
    }
    // Update primary signature info
    state.isSignatureMode = true;
    if (primaryRequest) {
        state.primarySignatureRequestId = primaryRequest.id;
        state.primarySignee = primaryRequest.signer;
        state.primarySignatureBlob = primaryRequest.signatureBlob;
        state.primarySignedDateTimeISO = primaryRequest.completedOnISO;
    }
    if (isNullificationCapableState(state)) {
        state.isNullificationMode = true;
        state.isNullificationViewMode = false;
    }
    // Set base signature completion status
    state.allSignaturesPreviouslySigned = !!primaryRequest?.completedOnISO;
    if (hasWitnessRequests && isWitnessSignatureState(state)) {
        const witnessRequest = signatureRequests.find(
            (sr) => sr.signatureType === ECSLSignatureRequestType.WITNESS,
        );
        if (witnessRequest) {
            const witnessState = state as IWitnessSignatureState;
            witnessState.witnessSignatureRequestId = witnessRequest.id;
            witnessState.witnessSignee = witnessRequest.signer;
            witnessState.witnessSignatureBlob = witnessRequest.signatureBlob;
            witnessState.witnessSignedDateTimeISO = witnessRequest.completedOnISO;
            // Update overall completion status
            state.allSignaturesPreviouslySigned =
                !!primaryRequest?.completedOnISO && !!witnessRequest.completedOnISO;
        }
    }
    return state;
};
